import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase, supabaseFunctionsUrl } from '../config/supabase';
import { useAuth } from '../context/AuthContext';
import { markActivityAsSeen } from '../services/adminActivityService';
import { sendPasswordResetEmail } from '../utils/emailJsPortal';
import Layout from '../components/Layout';
import { formatDate } from '../utils/formatters';
import './ClientDetail.css';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, role } = useAuth();
  const isAdmin = role === 'admin';
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [visibilityRows, setVisibilityRows] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [stakeholdersError, setStakeholdersError] = useState('');
  const [addStakeholderId, setAddStakeholderId] = useState('');
  const [addingVisibility, setAddingVisibility] = useState(false);
  const [removingVisibilityId, setRemovingVisibilityId] = useState(null);
  const [visibilityProductMap, setVisibilityProductMap] = useState({});
  const [editProductsUserId, setEditProductsUserId] = useState(null);
  const [productSelectionMode, setProductSelectionMode] = useState('all');
  const [productSelectionIds, setProductSelectionIds] = useState([]);
  const [savingProductVisibility, setSavingProductVisibility] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');
  const [amazonRegistry, setAmazonRegistry] = useState(null);
  const [togglingAmazon, setTogglingAmazon] = useState(false);
  const [showFullRegistry, setShowFullRegistry] = useState(false);
  const [registrySignedUrls, setRegistrySignedUrls] = useState({});
  const [loadingSignedUrls, setLoadingSignedUrls] = useState(false);

  const loadClient = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('client_profiles')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) {
        setError(fetchError.message || 'Client not found');
        setProfile(null);
        setProducts([]);
        setLoading(false);
        return;
      }

      setProfile(data);

      if (currentUser?.id) {
        markActivityAsSeen(currentUser.id, 'client_profile', id);
      }

      const { data: amazonData } = await supabase
        .from('amazon_brand_registry')
        .select('*')
        .eq('client_profile_id', id)
        .maybeSingle();
      setAmazonRegistry(amazonData || null);
      if (amazonData?.status === 'submitted' && currentUser?.id) {
        markActivityAsSeen(currentUser.id, 'amazon_registry', amazonData.id);
      }

      const { data: productsData } = await supabase
        .from('client_products')
        .select('id, product_name, sku_number, status, created_at')
        .eq('client_profile_id', id)
        .order('created_at', { ascending: false });
      setProducts(productsData || []);
    } catch (err) {
      setError(err.message || 'Failed to load client');
      setProfile(null);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [id, currentUser?.id]);

  useEffect(() => {
    if (!id) return;
    loadClient();
  }, [id, loadClient]);

  const loadVisibility = useCallback(async () => {
    if (!id || !isAdmin) return;
    const { data } = await supabase
      .from('client_visibility')
      .select('id, user_id')
      .eq('client_profile_id', id);
    setVisibilityRows(data || []);
  }, [id, isAdmin]);

  const loadVisibilityProducts = useCallback(async () => {
    if (!id || !isAdmin) return;
    const { data } = await supabase
      .from('client_visibility_products')
      .select('user_id, client_product_id')
      .eq('client_profile_id', id);
    const map = {};
    (data || []).forEach((row) => {
      if (!map[row.user_id]) map[row.user_id] = [];
      map[row.user_id].push(row.client_product_id);
    });
    setVisibilityProductMap(map);
  }, [id, isAdmin]);

  useEffect(() => {
    if (!id || !isAdmin) return;
    loadVisibility();
    loadVisibilityProducts();
  }, [id, isAdmin, loadVisibility, loadVisibilityProducts]);

  const loadStakeholders = useCallback(async () => {
    if (!isAdmin) return;
    setVisibilityLoading(true);
    setStakeholdersError('');
    try {
      if (supabaseFunctionsUrl) {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${supabaseFunctionsUrl}/get-stakeholders`, {
          headers: { Authorization: `Bearer ${session?.access_token}` },
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json.stakeholders) {
          setStakeholders(json.stakeholders);
          setVisibilityLoading(false);
          return;
        }
        if (!res.ok) {
          setStakeholdersError(json.error || `Failed to load (${res.status}). Using list below.`);
        }
      }
      // Fallback: load stakeholder user_ids and full_name from DB when Edge Function fails or isn't deployed
      const { data: rows } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'stakeholder');
      const userIds = (rows || []).map((r) => r.user_id);
      const { data: profiles } = userIds.length > 0
        ? await supabase.from('stakeholder_profiles').select('user_id, full_name').in('user_id', userIds)
        : { data: [] };
      const nameByUserId = {};
      (profiles || []).forEach((p) => { nameByUserId[p.user_id] = p.full_name || ''; });
      const list = userIds.map((uid) => ({ user_id: uid, email: '', full_name: nameByUserId[uid] || '' }));
      setStakeholders(list);
      if (list.length > 0 && (!!supabaseFunctionsUrl)) setStakeholdersError('');
    } catch (err) {
      console.warn('Failed to load stakeholders:', err);
      setStakeholdersError(err?.message || 'Network error. Check REACT_APP_SUPABASE_URL and that get-stakeholders is deployed.');
      setStakeholders([]);
    } finally {
      setVisibilityLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) loadStakeholders();
  }, [isAdmin, loadStakeholders]);

  const handleAddVisibility = async (e) => {
    e.preventDefault();
    const userId = addStakeholderId?.trim();
    if (!userId || !id) return;
    setAddingVisibility(true);
    try {
      const { error: insertErr } = await supabase
        .from('client_visibility')
        .insert({ client_profile_id: id, user_id: userId });
      if (!insertErr) {
        setAddStakeholderId('');
        await loadVisibility();
        await loadVisibilityProducts();
      }
    } finally {
      setAddingVisibility(false);
    }
  };

  const handleRemoveVisibility = async (row) => {
    setRemovingVisibilityId(row.id);
    try {
      await supabase.from('client_visibility').delete().eq('id', row.id);
      await supabase
        .from('client_visibility_products')
        .delete()
        .eq('client_profile_id', id)
        .eq('user_id', row.user_id);
      await loadVisibility();
      await loadVisibilityProducts();
    } finally {
      setRemovingVisibilityId(null);
    }
  };

  const openProductVisibilityModal = (row) => {
    const current = visibilityProductMap[row.user_id];
    const hasRestriction = current && current.length > 0;
    setEditProductsUserId(row.user_id);
    setProductSelectionMode(hasRestriction ? 'selected' : 'all');
    setProductSelectionIds(hasRestriction ? [...current] : []);
  };

  const closeProductVisibilityModal = () => {
    setEditProductsUserId(null);
    setProductSelectionMode('all');
    setProductSelectionIds([]);
  };

  const handleProductVisibilityToggle = (productId) => {
    setProductSelectionIds((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    );
  };

  const handleSaveProductVisibility = async () => {
    if (editProductsUserId == null || !id) return;
    setSavingProductVisibility(true);
    try {
      await supabase
        .from('client_visibility_products')
        .delete()
        .eq('client_profile_id', id)
        .eq('user_id', editProductsUserId);
      if (productSelectionMode === 'selected' && productSelectionIds.length > 0) {
        await supabase.from('client_visibility_products').insert(
          productSelectionIds.map((client_product_id) => ({
            client_profile_id: id,
            user_id: editProductsUserId,
            client_product_id,
          }))
        );
      }
      setVisibilityProductMap((prev) => ({
        ...prev,
        [editProductsUserId]: productSelectionMode === 'all' ? [] : productSelectionIds,
      }));
      closeProductVisibilityModal();
    } finally {
      setSavingProductVisibility(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="client-detail">
          <p className="client-detail-loading">Loading...</p>
        </div>
      </Layout>
    );
  }

  if (error || !profile) {
    return (
      <Layout>
        <div className="client-detail">
          <div className="client-detail-error">
            {error || 'Client not found'}
          </div>
          <Link to="/clients" className="client-detail-back">← Back to Clients</Link>
        </div>
      </Layout>
    );
  }

  const handleSendPasswordReset = async () => {
    if (!profile?.email) {
      setResetError('No email address found for this client.');
      return;
    }
    setSendingReset(true);
    setResetError('');
    setResetSent(false);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const redirectTo = `${window.location.origin}/reset-password`;
      const res = await fetch(`${supabaseFunctionsUrl}/generate-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ email: profile.email, redirectTo }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResetError(json.error || `Failed to generate reset link (${res.status})`);
        return;
      }
      const result = await sendPasswordResetEmail({ to_email: profile.email, link: json.link });
      if (!result.ok && !result.skipped) {
        setResetError(result.error || 'Failed to send reset email.');
        return;
      }
      setResetSent(true);
    } catch (err) {
      setResetError(err?.message || 'Failed to send password reset.');
    } finally {
      setSendingReset(false);
    }
  };

  const handleAmazonToggle = async () => {
    if (!isAdmin || !profile) return;
    setTogglingAmazon(true);
    const newValue = !profile.amazon_enabled;
    const { error: updateErr } = await supabase
      .from('client_profiles')
      .update({ amazon_enabled: newValue })
      .eq('id', profile.id);
    if (!updateErr) {
      setProfile((prev) => ({ ...prev, amazon_enabled: newValue }));
    }
    setTogglingAmazon(false);
  };

  const loadRegistrySignedUrls = async () => {
    if (!amazonRegistry) return;
    setLoadingSignedUrls(true);
    const fileFields = [
      'brand_logo_path',
      'product_image_1_path',
      'product_image_2_path',
      'product_image_3_path',
      'product_image_4_path',
      'product_image_5_path',
      'manufacturer_document_path',
      'supply_invoice_path',
    ];
    const urls = {};
    await Promise.all(
      fileFields.map(async (field) => {
        const path = amazonRegistry[field];
        if (!path) return;
        const { data } = await supabase.storage.from('client-portal-uploads').createSignedUrl(path, 3600);
        if (data?.signedUrl) urls[field] = data.signedUrl;
      })
    );
    setRegistrySignedUrls(urls);
    setLoadingSignedUrls(false);
  };

  const handleDeleteClick = () => {
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      const userId = profile.user_id;
      if (currentUser?.id && userId === currentUser.id) {
        setDeleteError('This client profile is linked to your own account. You cannot delete it from here — use a different admin or fix the data in the database.');
        setDeleting(false);
        return;
      }
      const { error: roleErr } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId);
      if (roleErr) {
        setDeleteError(roleErr.message || 'Failed to remove client role');
        setDeleting(false);
        return;
      }
      const { error: profileErr } = await supabase
        .from('client_profiles')
        .delete()
        .eq('id', id);
      if (profileErr) {
        setDeleteError(profileErr.message || 'Failed to delete client');
        setDeleting(false);
        return;
      }
      setShowDeleteModal(false);
      navigate('/clients');
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete client');
    }
    setDeleting(false);
  };

  const handleDeleteCancel = () => {
    if (!deleting) {
      setShowDeleteModal(false);
      setDeleteError('');
    }
  };

  return (
    <Layout>
      <div className="client-detail">
        <div className="client-detail-header">
          <div className="client-detail-header-top">
            <Link to="/clients" className="client-detail-back">← Back to Clients</Link>
            {isAdmin && (
              <button
                type="button"
                className="client-detail-delete-btn"
                onClick={handleDeleteClick}
                aria-label="Delete client"
              >
                Delete client
              </button>
            )}
          </div>
          <h1>{profile.company_name || 'Client'}</h1>
          <p className="client-detail-meta">
            Setup completed {profile.setup_completed_at ? formatDate(profile.setup_completed_at) : 'Not yet'}
          </p>
        </div>

        <section className="client-detail-section">
          <h2>Primary contact</h2>
          <dl className="client-detail-dl">
            <dt>First name</dt>
            <dd>{profile.first_name || '—'}</dd>
            <dt>Last name</dt>
            <dd>{profile.last_name || '—'}</dd>
            <dt>Job title</dt>
            <dd>{profile.job_title || '—'}</dd>
            <dt>Company name</dt>
            <dd>{profile.company_name || '—'}</dd>
            <dt>Email</dt>
            <dd>{profile.email ? <a href={`mailto:${profile.email}`}>{profile.email}</a> : '—'}</dd>
            <dt>Mobile phone</dt>
            <dd>{profile.mobile_phone ? <a href={`tel:${profile.mobile_phone}`}>{profile.mobile_phone}</a> : '—'}</dd>
            <dt>Secondary phone</dt>
            <dd>{profile.secondary_phone || '—'}</dd>
          </dl>
        </section>

        <section className="client-detail-section">
          <h2>Company / supplier details</h2>
          <dl className="client-detail-dl">
            <dt>Brand / supplier name</dt>
            <dd>{profile.brand_supplier_name || '—'}</dd>
            <dt>Address</dt>
            <dd>
              {[
                profile.address_line_1,
                profile.address_line_2,
                profile.address_line_3,
                [profile.town_city, profile.postcode].filter(Boolean).join(' '),
                profile.country,
              ].filter(Boolean).join(', ') || '—'}
            </dd>
          </dl>
        </section>

        <section className="client-detail-section">
          <h2>Ship from address</h2>
          <dl className="client-detail-dl">
            <dt>Address</dt>
            <dd>
              {profile.ship_from_same_as_address !== false
                ? 'Same as company address'
                : [
                    profile.ship_from_address_line_1,
                    profile.ship_from_address_line_2,
                    profile.ship_from_address_line_3,
                    [profile.ship_from_town_city, profile.ship_from_postcode].filter(Boolean).join(' '),
                    profile.ship_from_country,
                  ].filter(Boolean).join(', ') || '—'}
            </dd>
          </dl>
        </section>

        {isAdmin && (
          <section className="client-detail-section client-detail-visibility">
            <h2>Visibility</h2>
            <p className="client-detail-visibility-intro">
              Stakeholders who can see this client in their dashboard. Add a stakeholder to grant access.
            </p>
            <ul className="client-detail-visibility-list">
              {visibilityRows.map((row) => {
                const sh = stakeholders.find((s) => s.user_id === row.user_id);
                const productIds = visibilityProductMap[row.user_id];
                const seesAllProducts = !productIds || productIds.length === 0;
                const selectedProductNames = seesAllProducts
                  ? []
                  : (productIds || []).map(
                      (pid) => products.find((p) => p.id === pid)?.product_name || products.find((p) => p.id === pid)?.sku_number || pid
                    );
                return (
                  <li key={row.id} className="client-detail-visibility-item">
                    <div className="client-detail-visibility-item-main">
                      <span className="client-detail-visibility-name">{sh?.full_name || sh?.email || row.user_id}</span>
                      <button
                        type="button"
                        className="client-detail-visibility-remove"
                        onClick={() => handleRemoveVisibility(row)}
                        disabled={removingVisibilityId === row.id}
                      >
                        {removingVisibilityId === row.id ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                    <div className="client-detail-visibility-products-row">
                      <span className="client-detail-visibility-products-label">Products:</span>
                      {seesAllProducts ? (
                        <span className="client-detail-visibility-products-value">All</span>
                      ) : (
                        <span className="client-detail-visibility-products-value">
                          {selectedProductNames.length} selected
                        </span>
                      )}
                      <button
                        type="button"
                        className="client-detail-visibility-products-edit"
                        onClick={() => openProductVisibilityModal(row)}
                      >
                        Edit
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
            {visibilityRows.length === 0 && <p className="client-detail-visibility-empty">No stakeholders assigned.</p>}
            {stakeholdersError && (
              <div className="client-detail-visibility-error">
                <p>{stakeholdersError}</p>
                <button type="button" className="client-detail-visibility-retry" onClick={loadStakeholders}>
                  Retry
                </button>
              </div>
            )}
            {!stakeholdersError && stakeholders.length === 0 && !visibilityLoading && (
              <p className="client-detail-visibility-empty client-detail-visibility-hint">
                No stakeholders yet. Create one via <strong>Clients</strong> → <strong>Invite stakeholder</strong>. After they complete registration (using the link with <code>?type=stakeholder</code>), they will appear here. If you already invited someone but they don’t appear, redeploy the <strong>complete-registration</strong> and <strong>get-stakeholders</strong> Edge Functions.
              </p>
            )}
            <form onSubmit={handleAddVisibility} className="client-detail-visibility-add">
              <label htmlFor="visibility-stakeholder">Add stakeholder</label>
              <select
                id="visibility-stakeholder"
                value={addStakeholderId}
                onChange={(e) => setAddStakeholderId(e.target.value)}
                disabled={visibilityLoading}
              >
                <option value="">Select…</option>
                {stakeholders
                  .filter((s) => !visibilityRows.some((r) => r.user_id === s.user_id))
                  .map((s) => (
                    <option key={s.user_id} value={s.user_id}>
                      {s.full_name || s.email || s.user_id}
                    </option>
                  ))}
              </select>
              <button type="submit" className="client-detail-visibility-add-btn" disabled={!addStakeholderId || addingVisibility}>
                {addingVisibility ? 'Adding…' : 'Add'}
              </button>
            </form>
          </section>
        )}

        {isAdmin && (
          <section className="client-detail-section">
            <h2>Password reset</h2>
            <p className="client-detail-visibility-intro">
              Send the client a link to reset their password. They will receive an email with a secure link valid for one hour.
            </p>
            {resetSent && (
              <div className="client-detail-reset-success">
                Password reset link sent to {profile.email}.
              </div>
            )}
            {resetError && (
              <div className="client-detail-modal-error">{resetError}</div>
            )}
            <button
              type="button"
              className="client-detail-reset-btn"
              onClick={handleSendPasswordReset}
              disabled={sendingReset || !profile.email}
            >
              {sendingReset ? 'Sending…' : 'Send password reset link'}
            </button>
          </section>
        )}

        {isAdmin && (
          <section className="client-detail-section">
            <h2>Amazon</h2>
            <p className="client-detail-visibility-intro">
              Enable to require the client to complete the Amazon Brand Registry form. Once submitted, they can also add Amazon product listings from their dashboard.
            </p>
            <div className="client-detail-amazon-toggle-row">
              <span className="client-detail-amazon-toggle-label">Enable Amazon Brand Registry &amp; Product Upload</span>
              <button
                type="button"
                className={`client-detail-amazon-toggle${profile.amazon_enabled ? ' client-detail-amazon-toggle-on' : ''}`}
                onClick={handleAmazonToggle}
                disabled={togglingAmazon}
                aria-pressed={profile.amazon_enabled}
              >
                <span className="client-detail-amazon-toggle-thumb" />
              </button>
              <span className="client-detail-amazon-toggle-state">
                {profile.amazon_enabled ? 'On' : 'Off'}
              </span>
            </div>

            {profile.amazon_enabled && (
              <div className="client-detail-amazon-status">
                {amazonRegistry?.status === 'submitted' ? (
                  <>
                    <p className="client-detail-amazon-submitted-label">
                      Submitted {amazonRegistry.submitted_at ? formatDate(amazonRegistry.submitted_at) : ''}
                    </p>
                    <dl className="client-detail-dl client-detail-amazon-dl">
                      <dt>Trademark Name</dt><dd>{amazonRegistry.brand_trademark_name || '—'}</dd>
                      <dt>Trademark Office</dt><dd>{amazonRegistry.trademark_office || '—'}</dd>
                      <dt>Registration Number</dt><dd>{amazonRegistry.trademark_registration_number || '—'}</dd>
                      <dt>Category</dt><dd>{amazonRegistry.brand_category || '—'}</dd>
                      <dt>Website</dt><dd>{amazonRegistry.brand_website_url || '—'}</dd>
                      <dt>Business Address</dt><dd>{amazonRegistry.registered_business_address || '—'}</dd>
                      <dt>EIN / VAT</dt><dd>{amazonRegistry.ein_or_vat_number || '—'}</dd>
                      <dt>Trademark Contact</dt>
                      <dd>
                        {[amazonRegistry.trademark_contact_name, amazonRegistry.trademark_contact_email, amazonRegistry.trademark_contact_phone]
                          .filter(Boolean).join(' · ') || '—'}
                      </dd>
                      <dt>Sells to Distributors</dt>
                      <dd>
                        {amazonRegistry.sells_to_distributors === null ? '—' : amazonRegistry.sells_to_distributors ? 'Yes' : 'No'}
                        {amazonRegistry.sells_to_distributors && amazonRegistry.distribution_locations && (
                          <> — {amazonRegistry.distribution_locations}</>
                        )}
                      </dd>
                      <dt>Licenses Trademarks</dt>
                      <dd>
                        {amazonRegistry.licenses_trademarks === null ? '—' : amazonRegistry.licenses_trademarks ? 'Yes' : 'No'}
                        {amazonRegistry.licenses_trademarks && amazonRegistry.trademark_licensee_description && (
                          <> — {amazonRegistry.trademark_licensee_description}</>
                        )}
                      </dd>
                    </dl>
                    <button
                      type="button"
                      className="client-detail-amazon-full-btn"
                      onClick={() => { setShowFullRegistry(true); loadRegistrySignedUrls(); }}
                    >
                      View full submission
                    </button>
                  </>
                ) : (
                  <p className="client-detail-amazon-pending">
                    {amazonRegistry?.status === 'draft' ? 'Client has saved a draft.' : 'Awaiting client submission.'}
                  </p>
                )}
              </div>
            )}
          </section>
        )}

        <p className="client-detail-updated">
          Last updated {formatDate(profile.updated_at)} · Created {formatDate(profile.created_at)}
        </p>

        <section className="client-detail-section client-detail-products">
          <h2>Products ({products.length})</h2>
          {products.length === 0 ? (
            <p className="client-detail-no-products">No products submitted yet.</p>
          ) : (
            <div className="client-detail-table-wrap">
              <table className="client-detail-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr
                      key={product.id}
                      className="client-detail-product-row"
                      onClick={() => navigate(`/clients/${id}/product/${product.id}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/clients/${id}/product/${product.id}`);
                        }
                      }}
                    >
                      <td><strong>{product.product_name || '—'}</strong></td>
                      <td>{product.sku_number || '—'}</td>
                      <td>
                        <span className={`client-detail-status client-detail-status-${(product.status || '').toLowerCase()}`}>
                          {(product.status || 'draft').toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {showFullRegistry && amazonRegistry && (
          <div className="client-detail-modal-overlay" onClick={() => setShowFullRegistry(false)} role="dialog" aria-modal="true" aria-labelledby="full-registry-modal-title">
            <div className="client-detail-modal client-detail-amazon-full-modal" onClick={(e) => e.stopPropagation()}>
              <div className="client-detail-amazon-full-header">
                <h2 id="full-registry-modal-title" className="client-detail-modal-title">Amazon Brand Registry Submission</h2>
                <button type="button" className="client-detail-modal-cancel" onClick={() => setShowFullRegistry(false)}>Close</button>
              </div>

              <div className="client-detail-amazon-full-section">
                <h3 className="client-detail-amazon-full-section-title">Brand Information</h3>
                <dl className="client-detail-dl client-detail-amazon-dl">
                  <dt>Trademark Name</dt><dd>{amazonRegistry.brand_trademark_name || '—'}</dd>
                  <dt>Trademark Office</dt><dd>{amazonRegistry.trademark_office || '—'}</dd>
                  <dt>Registration Number</dt><dd>{amazonRegistry.trademark_registration_number || '—'}</dd>
                  <dt>Category</dt><dd>{amazonRegistry.brand_category || '—'}</dd>
                  <dt>Website</dt><dd>{amazonRegistry.brand_website_url ? <a href={amazonRegistry.brand_website_url} target="_blank" rel="noopener noreferrer">{amazonRegistry.brand_website_url}</a> : '—'}</dd>
                </dl>
                {loadingSignedUrls ? (
                  <p className="client-detail-amazon-full-loading">Loading files…</p>
                ) : registrySignedUrls.brand_logo_path ? (
                  <div className="client-detail-amazon-full-logo-wrap">
                    <p className="client-detail-amazon-full-file-label">Brand Logo</p>
                    <img src={registrySignedUrls.brand_logo_path} alt="Brand logo" className="client-detail-amazon-full-logo" />
                  </div>
                ) : amazonRegistry.brand_logo_path ? (
                  <p className="client-detail-amazon-full-file-label">Brand logo: <em>Unable to load</em></p>
                ) : null}
              </div>

              <div className="client-detail-amazon-full-section">
                <h3 className="client-detail-amazon-full-section-title">Product Images</h3>
                {loadingSignedUrls ? (
                  <p className="client-detail-amazon-full-loading">Loading images…</p>
                ) : (
                  <div className="client-detail-amazon-images-grid">
                    {[1, 2, 3, 4, 5].map((n) => {
                      const field = `product_image_${n}_path`;
                      const url = registrySignedUrls[field];
                      const hasPath = !!amazonRegistry[field];
                      if (!hasPath) return null;
                      return (
                        <div key={n} className="client-detail-amazon-image-item">
                          <p className="client-detail-amazon-full-file-label">Image {n}</p>
                          {url ? (
                            <a href={url} target="_blank" rel="noopener noreferrer">
                              <img src={url} alt={`Product ${n}`} className="client-detail-amazon-product-img" />
                            </a>
                          ) : (
                            <em>Unable to load</em>
                          )}
                        </div>
                      );
                    })}
                    {![1,2,3,4,5].some((n) => amazonRegistry[`product_image_${n}_path`]) && <p>No product images uploaded.</p>}
                  </div>
                )}
              </div>

              <div className="client-detail-amazon-full-section">
                <h3 className="client-detail-amazon-full-section-title">Documents</h3>
                {loadingSignedUrls ? (
                  <p className="client-detail-amazon-full-loading">Loading documents…</p>
                ) : (
                  <div className="client-detail-amazon-docs">
                    {registrySignedUrls.manufacturer_document_path ? (
                      <a href={registrySignedUrls.manufacturer_document_path} target="_blank" rel="noopener noreferrer" className="client-detail-amazon-file-link">
                        Manufacturer Document
                      </a>
                    ) : amazonRegistry.manufacturer_document_path ? (
                      <span>Manufacturer Document: <em>Unable to load</em></span>
                    ) : (
                      <span className="client-detail-amazon-no-file">No manufacturer document uploaded.</span>
                    )}
                    {registrySignedUrls.supply_invoice_path ? (
                      <a href={registrySignedUrls.supply_invoice_path} target="_blank" rel="noopener noreferrer" className="client-detail-amazon-file-link">
                        Supply Invoice
                      </a>
                    ) : amazonRegistry.supply_invoice_path ? (
                      <span>Supply Invoice: <em>Unable to load</em></span>
                    ) : (
                      <span className="client-detail-amazon-no-file">No supply invoice uploaded.</span>
                    )}
                  </div>
                )}
              </div>

              <div className="client-detail-amazon-full-section">
                <h3 className="client-detail-amazon-full-section-title">Business Information</h3>
                <dl className="client-detail-dl client-detail-amazon-dl">
                  <dt>Business Address</dt><dd>{amazonRegistry.registered_business_address || '—'}</dd>
                  <dt>EIN / VAT</dt><dd>{amazonRegistry.ein_or_vat_number || '—'}</dd>
                  <dt>Contact Name</dt><dd>{amazonRegistry.trademark_contact_name || '—'}</dd>
                  <dt>Contact Email</dt><dd>{amazonRegistry.trademark_contact_email || '—'}</dd>
                  <dt>Contact Phone</dt><dd>{amazonRegistry.trademark_contact_phone || '—'}</dd>
                </dl>
              </div>

              <div className="client-detail-amazon-full-section">
                <h3 className="client-detail-amazon-full-section-title">Distribution &amp; Licensing</h3>
                <dl className="client-detail-dl client-detail-amazon-dl">
                  <dt>Sells to Distributors</dt>
                  <dd>
                    {amazonRegistry.sells_to_distributors === null ? '—' : amazonRegistry.sells_to_distributors ? 'Yes' : 'No'}
                    {amazonRegistry.sells_to_distributors && amazonRegistry.distribution_locations && (
                      <><br />{amazonRegistry.distribution_locations}</>
                    )}
                  </dd>
                  <dt>Licenses Trademarks</dt>
                  <dd>
                    {amazonRegistry.licenses_trademarks === null ? '—' : amazonRegistry.licenses_trademarks ? 'Yes' : 'No'}
                    {amazonRegistry.licenses_trademarks && amazonRegistry.trademark_licensee_description && (
                      <><br />{amazonRegistry.trademark_licensee_description}</>
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && (
          <div className="client-detail-modal-overlay" onClick={handleDeleteCancel} role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
            <div className="client-detail-modal" onClick={(e) => e.stopPropagation()}>
              <h2 id="delete-modal-title" className="client-detail-modal-title">Delete this client?</h2>
              <p className="client-detail-modal-warning">
                This will permanently delete the client account and all related data (profile, products, and any uploaded files). They will no longer be able to log in. This cannot be undone.
              </p>
              {deleteError && <div className="client-detail-modal-error">{deleteError}</div>}
              <div className="client-detail-modal-actions">
                <button
                  type="button"
                  className="client-detail-modal-cancel"
                  onClick={handleDeleteCancel}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="client-detail-modal-confirm"
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting…' : 'Delete client'}
                </button>
              </div>
            </div>
          </div>
        )}

        {editProductsUserId != null && (
          <div
            className="client-detail-modal-overlay"
            onClick={closeProductVisibilityModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="products-modal-title"
          >
            <div className="client-detail-modal client-detail-products-modal" onClick={(e) => e.stopPropagation()}>
              <h2 id="products-modal-title" className="client-detail-modal-title">
                Which products can this stakeholder see?
              </h2>
              <div className="client-detail-products-modal-options">
                <label className="client-detail-products-modal-radio">
                  <input
                    type="radio"
                    name="productScope"
                    checked={productSelectionMode === 'all'}
                    onChange={() => setProductSelectionMode('all')}
                  />
                  <span>All products</span>
                </label>
                <label className="client-detail-products-modal-radio">
                  <input
                    type="radio"
                    name="productScope"
                    checked={productSelectionMode === 'selected'}
                    onChange={() => setProductSelectionMode('selected')}
                  />
                  <span>Only selected products</span>
                </label>
              </div>
              {productSelectionMode === 'selected' && (
                <div className="client-detail-products-modal-list">
                  {products.length === 0 ? (
                    <p className="client-detail-products-modal-empty">No products yet. Add products to this client first.</p>
                  ) : (
                    products.map((p) => (
                      <label key={p.id} className="client-detail-products-modal-checkbox">
                        <input
                          type="checkbox"
                          checked={productSelectionIds.includes(p.id)}
                          onChange={() => handleProductVisibilityToggle(p.id)}
                        />
                        <span>{p.product_name || p.sku_number || p.id}</span>
                      </label>
                    ))
                  )}
                </div>
              )}
              <div className="client-detail-modal-actions">
                <button
                  type="button"
                  className="client-detail-modal-cancel"
                  onClick={closeProductVisibilityModal}
                  disabled={savingProductVisibility}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="client-detail-modal-confirm"
                  onClick={handleSaveProductVisibility}
                  disabled={savingProductVisibility || (productSelectionMode === 'selected' && productSelectionIds.length === 0)}
                >
                  {savingProductVisibility ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ClientDetail;
